<template>
  <div class="page-container">
    <el-card class="search-card">
      <el-row :gutter="20">
        <el-col :span="6">
          <el-input 
            v-model="searchKeyword" 
            placeholder="搜索资源标题" 
            clearable
            @clear="handleSearch"
            @keyup.enter.native="handleSearch">
            <el-button slot="append" icon="el-icon-search" @click="handleSearch"></el-button>
          </el-input>
        </el-col>
        <el-col :span="4">
          <el-select v-model="statusFilter" placeholder="状态筛选" clearable @change="handleSearch">
            <el-option label="全部" :value="null"></el-option>
            <el-option label="待审核" :value="0"></el-option>
            <el-option label="已通过" :value="1"></el-option>
            <el-option label="已拒绝" :value="3"></el-option>
          </el-select>
        </el-col>
      </el-row>
    </el-card>

    <el-card>
      <div slot="header" class="card-header">
        <span><i class="el-icon-document-checked"></i> {{ pageTitle }}</span>
        <div class="header-stats">
          <el-tag type="warning">待审核: {{ pendingCount }}</el-tag>
        </div>
      </div>

      <el-table :data="tableData" border v-loading="loading">
        <el-table-column prop="id" label="ID" width="80"></el-table-column>
        <el-table-column prop="title" label="资源标题" min-width="200" show-overflow-tooltip></el-table-column>
        <el-table-column label="封面" width="100">
          <template slot-scope="scope">
            <el-image 
              v-if="scope.row.coverImage"
              :src="scope.row.coverImage" 
              style="width: 60px; height: 60px; border-radius: 4px;"
              fit="cover">
            </el-image>
            <el-image 
              v-else
              src="/images/default-cover.png"
              style="width: 60px; height: 60px; border-radius: 4px;"
              fit="cover">
            </el-image>
          </template>
        </el-table-column>
        <el-table-column label="资源类型" width="100">
          <template slot-scope="scope">
            {{ getResourceTypeName(scope.row.resourceType) }}
          </template>
        </el-table-column>
        <el-table-column label="提交用户" width="150">
          <template slot-scope="scope">
            <div style="display: flex; flex-direction: column; gap: 4px;">
              <span style="font-weight: 500;">{{ scope.row.frontUserName || '未知用户' }}</span>
              <el-tag size="mini" type="info">ID: {{ scope.row.frontUserId }}</el-tag>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="状态" width="100">
          <template slot-scope="scope">
            <el-tag v-if="scope.row.status === 0" type="warning">待审核</el-tag>
            <el-tag v-else-if="scope.row.status === 1" type="success">已通过</el-tag>
            <el-tag v-else-if="scope.row.status === 3" type="danger">已拒绝</el-tag>
            <el-tag v-else type="info">草稿</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="createTime" label="提交时间" width="160">
          <template slot-scope="scope">
            {{ formatDate(scope.row.createTime) }}
          </template>
        </el-table-column>
        <el-table-column label="操作" width="250" fixed="right">
          <template slot-scope="scope">
            <el-button 
              size="mini" 
              type="text" 
              @click="handleView(scope.row)"
              icon="el-icon-view">
              查看
            </el-button>
            <el-button 
              v-if="scope.row.status === 0"
              size="mini" 
              type="text" 
              @click="handleApprove(scope.row)"
              icon="el-icon-check"
              style="color: #67C23A;">
              通过
            </el-button>
            <el-button 
              v-if="scope.row.status === 0"
              size="mini" 
              type="text" 
              @click="handleReject(scope.row)"
              icon="el-icon-close"
              style="color: #F56C6C;">
              拒绝
            </el-button>
            <el-button 
              size="mini" 
              type="text" 
              @click="handleDelete(scope.row)"
              icon="el-icon-delete"
              style="color: #F56C6C;">
              删除
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <el-pagination
        v-if="total > 0"
        @current-change="handlePageChange"
        @size-change="handleSizeChange"
        :current-page="page.current"
        :page-sizes="[10, 20, 50, 100]"
        :page-size="page.size"
        layout="total, sizes, prev, pager, next, jumper"
        :total="total"
        style="text-align: center; margin-top: 20px;">
      </el-pagination>
    </el-card>

    <!-- 查看资源详情对话框 -->
    <el-dialog 
      title="资源详情" 
      :visible.sync="viewDialogVisible" 
      width="800px"
      :close-on-click-modal="false"
      @close="currentResource = null">
      <div v-if="currentResource" class="resource-detail">
        <el-descriptions :column="2" border>
          <el-descriptions-item label="资源标题">{{ currentResource.title }}</el-descriptions-item>
          <el-descriptions-item label="资源类型">{{ getResourceTypeName(currentResource.resourceType) }}</el-descriptions-item>
          <el-descriptions-item label="分类ID">{{ currentResource.categoryId }}</el-descriptions-item>
          <el-descriptions-item label="提交用户">
            <div>
              <div style="font-weight: 500;">{{ currentResource.frontUserName || '未知用户' }}</div>
              <el-tag size="small" type="info" style="margin-top: 4px;">ID: {{ currentResource.frontUserId }}</el-tag>
            </div>
          </el-descriptions-item>
          <el-descriptions-item label="是否免费">
            <el-tag :type="currentResource.isFree === 1 ? 'success' : 'warning'" size="small">
              {{ currentResource.isFree === 1 ? '免费' : '付费' }}
            </el-tag>
          </el-descriptions-item>
          <el-descriptions-item label="价格" v-if="currentResource.isFree === 0">
            ¥{{ currentResource.price }}
          </el-descriptions-item>
          <el-descriptions-item label="提交时间">{{ formatDate(currentResource.createTime) }}</el-descriptions-item>
          <el-descriptions-item label="状态">
            <el-tag v-if="currentResource.status === 0" type="warning">待审核</el-tag>
            <el-tag v-else-if="currentResource.status === 1" type="success">已通过</el-tag>
            <el-tag v-else-if="currentResource.status === 3" type="danger">已拒绝</el-tag>
          </el-descriptions-item>
        </el-descriptions>

        <el-divider content-position="left">资源描述</el-divider>
        <div class="resource-description">{{ currentResource.description || '无描述' }}</div>

        <el-divider content-position="left">资源内容</el-divider>
        <div class="resource-content" v-html="currentResource.content || '无内容'"></div>

        <el-divider content-position="left">封面图片</el-divider>
        <el-image 
          v-if="currentResource.coverImage"
          :src="currentResource.coverImage" 
          style="max-width: 100%; border-radius: 8px;"
          fit="contain">
        </el-image>
        <div v-else style="color: #909399;">无封面图片</div>
      </div>
      <div slot="footer">
        <el-button @click="viewDialogVisible = false">关闭</el-button>
        <el-button 
          v-if="currentResource && currentResource.status === 0"
          type="success" 
          @click="handleApprove(currentResource)"
          icon="el-icon-check">
          通过审核
        </el-button>
        <el-button 
          v-if="currentResource && currentResource.status === 0"
          type="danger" 
          @click="handleReject(currentResource)"
          icon="el-icon-close">
          拒绝审核
        </el-button>
      </div>
    </el-dialog>

    <!-- 拒绝原因对话框 -->
    <el-dialog 
      title="拒绝审核" 
      :visible.sync="rejectDialogVisible" 
      width="500px"
      :close-on-click-modal="false">
      <el-form :model="rejectForm" :rules="rejectRules" ref="rejectForm" label-width="100px">
        <el-form-item label="资源标题">
          <span>{{ rejectResource ? rejectResource.title : '' }}</span>
        </el-form-item>
        <el-form-item label="拒绝原因" prop="reason">
          <el-input
            v-model="rejectForm.reason"
            type="textarea"
            :rows="4"
            maxlength="200"
            show-word-limit
            placeholder="请输入拒绝原因，将通知提交用户">
          </el-input>
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="rejectDialogVisible = false">取消</el-button>
        <el-button type="danger" @click="confirmReject" :loading="rejecting">确认拒绝</el-button>
      </div>
    </el-dialog>
    
    <!-- 审核通过对话框 -->
    <el-dialog 
      title="审核通过" 
      :visible.sync="approveDialogVisible" 
      width="500px"
      :close-on-click-modal="false">
      <el-form label-width="120px">
        <el-form-item label="资源标题">
          <span>{{ approveResource ? approveResource.title : '' }}</span>
        </el-form-item>
        <el-form-item label="奖励下载次数">
          <el-input-number 
            v-model="rewardDownloadCount" 
            :min="1" 
            :max="100"
            controls-position="right"
            style="width: 200px;">
          </el-input-number>
          <div style="margin-top: 10px; color: #909399; font-size: 12px;">
            <i class="el-icon-info"></i> 额外下载次数永久有效，优先使用当日次数，用完再使用额外次数
          </div>
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="approveDialogVisible = false">取消</el-button>
        <el-button type="success" @click="confirmApprove">确认通过</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
export default {
  name: 'ResourceReview',
  data() {
    return {
      pageTitle: '资源审核', // 默认标题
      tableData: [],
      loading: false,
      searchKeyword: '',
      statusFilter: 0, // 默认显示待审核
      page: {
        current: 1,
        size: 10
      },
      total: 0,
      pendingCount: 0,
      viewDialogVisible: false,
      currentResource: null,
      rejectDialogVisible: false,
      rejectResource: null,
      rejectForm: {
        reason: ''
      },
      rejectRules: {
        reason: [
          { required: true, message: '请输入拒绝原因', trigger: 'blur' },
          { min: 5, max: 200, message: '原因长度在 5 到 200 个字符', trigger: 'blur' }
        ]
      },
      rejecting: false,
      approveDialogVisible: false,
      approveResource: null,
      rewardDownloadCount: 1
    }
  },
  mounted() {
    this.loadPageTitle()
    this.loadData()
    this.loadPendingCount()
  },
  methods: {
    // 动态加载页面标题
    async loadPageTitle() {
      try {
        const menuData = localStorage.getItem('admin_menu_data')
        if (menuData) {
          const menus = JSON.parse(menuData)
          const allMenus = menus.menus || []
          const currentMenu = allMenus.find(m => m.url === this.$route.path)
          if (currentMenu) {
            this.pageTitle = currentMenu.name
          }
        }
      } catch (error) {
        console.error('加载页面标题失败:', error)
      }
    },
    async loadData() {
      this.loading = true
      try {
        const res = await this.$axios.get('/admin/resource/review/page', {
          params: {
            current: this.page.current,
            size: this.page.size,
            keyword: this.searchKeyword,
            status: this.statusFilter
          }
        })
        if (res.data.code === 200) {
          this.tableData = res.data.data.records
          this.total = res.data.data.total
        }
      } catch (error) {
        this.$message.error('加载失败')
      } finally {
        this.loading = false
      }
    },
    async loadPendingCount() {
      try {
        const res = await this.$axios.get('/admin/resource/review/page', {
          params: {
            current: 1,
            size: 1,
            status: 0
          }
        })
        if (res.data.code === 200) {
          this.pendingCount = res.data.data.total
        }
      } catch (error) {
        console.error('加载待审核数量失败:', error)
      }
    },
    handleSearch() {
      this.page.current = 1
      this.loadData()
    },
    handlePageChange(page) {
      this.page.current = page
      this.loadData()
    },
    handleSizeChange(size) {
      this.page.size = size
      this.page.current = 1
      this.loadData()
    },
    handleView(row) {
      this.currentResource = row
      this.viewDialogVisible = true
    },
    handleApprove(row) {
      this.approveResource = row
      this.rewardDownloadCount = 1
      this.approveDialogVisible = true
    },
    async confirmApprove() {
      try {
        const res = await this.$axios.post(`/admin/resource/review/${this.approveResource.id}`, {
          status: 1,
          rewardDownloadCount: this.rewardDownloadCount
        })
        if (res.data.code === 200) {
          this.$message.success(`审核通过成功，已发送通知并奖励${this.rewardDownloadCount}次额外下载机会`)
          
          // 立即更新当前表格中的数据状态，使用$set确保响应式更新
          const index = this.tableData.findIndex(item => item.id === this.approveResource.id)
          if (index !== -1) {
            this.$set(this.tableData, index, {
              ...this.tableData[index],
              status: 1
            })
          }
          
          // 如果当前查看的资源也是这个，同步更新
          if (this.currentResource && this.currentResource.id === this.approveResource.id) {
            this.currentResource.status = 1
          }
          
          this.approveDialogVisible = false
          this.viewDialogVisible = false
          
          // 刷新数据和待审核数量
          this.loadData()
          this.loadPendingCount()
        } else {
          this.$message.error(res.data.message || '操作失败')
        }
      } catch (error) {
        console.error('审核失败:', error)
        this.$message.error('操作失败')
      }
    },
    handleReject(row) {
      this.rejectResource = row
      this.rejectForm.reason = ''
      this.rejectDialogVisible = true
    },
    confirmReject() {
      this.$refs.rejectForm.validate(async (valid) => {
        if (valid) {
          this.rejecting = true
          try {
            const res = await this.$axios.post(`/admin/resource/review/${this.rejectResource.id}`, {
              status: 3,
              rejectReason: this.rejectForm.reason
            })
            if (res.data.code === 200) {
              this.$message.success('已拒绝该资源，已发送通知给用户')
              
              // 立即更新当前表格中的数据状态，使用$set确保响应式更新
              const index = this.tableData.findIndex(item => item.id === this.rejectResource.id)
              if (index !== -1) {
                this.$set(this.tableData, index, {
                  ...this.tableData[index],
                  status: 3
                })
              }
              
              // 如果当前查看的资源也是这个，同步更新
              if (this.currentResource && this.currentResource.id === this.rejectResource.id) {
                this.currentResource.status = 3
              }
              
              this.rejectDialogVisible = false
              this.viewDialogVisible = false
              
              // 刷新数据和待审核数量
              this.loadData()
              this.loadPendingCount()
            } else {
              this.$message.error(res.data.message || '操作失败')
            }
          } catch (error) {
            console.error('审核失败:', error)
            this.$message.error('操作失败')
          } finally {
            this.rejecting = false
          }
        }
      })
    },
    handleDelete(row) {
      this.$confirm('确认要删除该资源吗？删除后前端用户也无法查看。', '删除资源', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          const res = await this.$axios.delete(`/admin/resource/${row.id}`)
          if (res.data.code === 200) {
            this.$message.success('删除成功')
            this.viewDialogVisible = false
            this.loadData()
            this.loadPendingCount()
          } else {
            this.$message.error(res.data.message || '删除失败')
          }
        } catch (error) {
          this.$message.error('删除失败')
        }
      }).catch(() => {})
    },
    getResourceTypeName(type) {
      const map = { 0: '软件', 1: '网站', 2: '视频教程', 3: '图文教程', 4: '其他' }
      return map[type] || '未知'
    },
    formatDate(date) {
      if (!date) return ''
      return new Date(date).toLocaleString('zh-CN')
    }
  }
}
</script>

<style scoped>
.page-container {
  padding: 20px;
  background: transparent;
  min-height: calc(100vh - 60px);
}

.search-card {
  margin-bottom: 20px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-stats {
  display: flex;
  gap: 10px;
}

.resource-detail {
  max-height: 600px;
  overflow-y: auto;
}

.resource-description,
.resource-content {
  padding: 15px;
  background: #f5f7fa;
  border-radius: 4px;
  line-height: 1.6;
  color: #606266;
}

.resource-content {
  min-height: 100px;
}
</style>
